import type { LucideIcon } from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/cn";

/**
 * ``flow`` — borderless section (the new default since Jun 13, 2026).
 *   Use this when sections stack vertically in a single column. Wrap
 *   them in a plain ``<div className="flex flex-col">``: the panel's
 *   own ``py-7`` padding handles the rhythm. **No hairline between
 *   siblings** (user feedback: "togli la linea che divide al centro").
 *   Sections are differentiated by typography + breathing space alone
 *   — same approach as Linear 4.x / Notion 2025 / Anthropic Console.
 *
 * ``card`` — bordered card (the legacy treatment).
 *   Reach for this when sections live SIDE-BY-SIDE in a grid (e.g. the
 *   3-up Marp panels). Without the card chrome the typography reads as
 *   orphan boxes floating without container.
 */
export type SettingsPanelVariant = "flow" | "card";

export interface SettingsPanelProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  /** Optional Lucide icon rendered before the title (h-4 w-4, text-ink-3). */
  readonly icon?: LucideIcon;
  /** Section title — rendered in ``font-display`` semibold 15px. */
  readonly title?: string | React.ReactNode;
  /** Optional supporting copy under the title (text-[12.5px] / text-ink-3). */
  readonly description?: string | React.ReactNode;
  /** Right-aligned actions slot (filter toggle, "Aggiungi", primary CTA). */
  readonly actions?: React.ReactNode;
  /** Visual treatment — see ``SettingsPanelVariant``. Default ``"flow"``. */
  readonly variant?: SettingsPanelVariant;
  /** Panel body. */
  readonly children: React.ReactNode;
  readonly className?: string;
}

/**
 * SettingsPanel — settings/admin section.
 *
 * Two visual treatments via ``variant``:
 *
 *   - ``flow`` (default) — Linear 4.x / Notion 2025 / Granola / Anthropic
 *     Console / Arc / Cursor 2026 pattern. Borderless section, hairline
 *     divider between siblings, ``font-display`` Space Grotesk section
 *     title (same family as ``SurfaceHeader``, one tier smaller — closes
 *     the typography gap between the 26px page title and the body text).
 *   - ``card`` — legacy ``rounded-xl border border-hairline bg-card/55``
 *     treatment. Kept for grid layouts where siblings stand side-by-side
 *     and need container chrome to read as discrete units.
 *
 * In ``flow`` mode the divider hairlines are painted by the PARENT stack
 * (``<div className="divide-y divide-hairline">``) so the first section
 * doesn't get a stray top hairline and the last doesn't get a bottom one.
 */
export const SettingsPanel = React.forwardRef<HTMLElement, SettingsPanelProps>(
  (
    {
      icon: Icon,
      title,
      description,
      actions,
      variant = "flow",
      children,
      className,
      ...rest
    },
    ref,
  ) => {
    const hasHeader = Boolean(Icon || title || description || actions);
    const isCard = variant === "card";
    return (
      <section
        ref={ref}
        className={cn(
          isCard
            ? "rounded-xl border border-hairline bg-card/55 p-5 shadow-sm"
            : "py-7 first:pt-2 last:pb-2",
          className,
        )}
        {...rest}
      >
        {hasHeader ? (
          <header
            className={cn(
              "flex items-start gap-3",
              isCard ? "mb-3.5 gap-2" : "mb-4",
            )}
          >
            {Icon ? (
              <span
                aria-hidden
                className={cn("text-ink-3", isCard ? "mt-0.5" : "mt-[3px]")}
              >
                <Icon
                  className={cn(isCard ? "h-3.5 w-3.5" : "h-4 w-4")}
                  strokeWidth={isCard ? 2 : 1.75}
                />
              </span>
            ) : null}
            {title || description ? (
              <div className="flex min-w-0 flex-col gap-1">
                {title ? (
                  <h2
                    className={cn(
                      "text-ink-1",
                      isCard
                        ? "font-semibold text-[13px]"
                        : "font-display font-semibold text-[15px] tracking-[-0.012em]",
                    )}
                  >
                    {title}
                  </h2>
                ) : null}
                {description ? (
                  <p
                    className={cn(
                      "text-ink-3",
                      isCard
                        ? "text-[11.5px]"
                        : "text-[12.5px] leading-relaxed",
                    )}
                  >
                    {description}
                  </p>
                ) : null}
              </div>
            ) : null}
            {actions ? (
              <div className="ml-auto flex shrink-0 items-center gap-1.5">
                {actions}
              </div>
            ) : null}
          </header>
        ) : null}
        {children}
      </section>
    );
  },
);
SettingsPanel.displayName = "SettingsPanel";
